import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  faChevronLeft,
  faChevronRight,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { SortableContext, rectSwappingStrategy } from '@dnd-kit/sortable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Badge } from '@repo/videa-components/src/ui/badge';
import classNames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import { isEqual } from 'lodash';
import { useParams } from 'react-router-dom';

import type { TVisitSummary } from '@/react-query/useVisitSummaries';

import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import annotatorConstants from '../../annotator.constants';
import { imageFilterAtom } from '../../atoms';
import type { ImageTypes } from '../findingViewer/imageBrowser/atoms';
import {
  enabledImageTypesAtom,
  showingAllAtom,
} from '../findingViewer/imageBrowser/atoms';
import {
  getImageBrowserDisplayImages,
  useExtraImages,
  useGetFilteredImages,
  useGetImageTypes,
} from '../findingViewer/imageBrowser/helpers';
import { DroppableContainer } from '../findingViewer/imageBrowser/imageBucket/droppableContainer/DroppableContainer';
import { Image } from '../findingViewer/imageBrowser/imageBucket/image/Image';
import { isExpandedAtom } from '../imageToolbar/atoms';

// Constants
const MAX_IMAGES_PER_PAGE = 14;

export const ImageGrid = ({
  visitSummary,
  onPaginationChange,
  currentPage,
  onPageChange,
}: {
  visitSummary?: TVisitSummary;
  onPaginationChange?: (data: {
    currentPage: number;
    totalPages: number;
    hasImages: boolean;
  }) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
}) => {
  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();

  // Atoms/States
  const [numberOfImagesToDisplay, setNumberOfImagesToDisplay] =
    useState<number>(MAX_IMAGES_PER_PAGE);
  const [filterSectionWidth, setFilterSectionWidth] = useState(0);
  const isShowingAll = useAtomValue(showingAllAtom);
  const [enabledImageTypes, setEnabledImageTypes] = useAtom(
    enabledImageTypesAtom,
  );
  const [isExpanded, setIsExpanded] = useAtom(isExpandedAtom);

  const prevImageTypes = useRef<Record<ImageTypes, boolean>>({
    dentalPhotos: false,
    panoPhotos: false,
    fmxPhotos: false,
    otherPhotos: false,
    fmxAsOther: false,
  });

  // ref to track if we've already decided to expand/collapse the image browser
  const hasDecidedExpandedState = useRef(false);

  const [hasInitializedPage, setHasInitializedPage] = useState(false);
  const [lastActiveImageId, setLastActiveImageId] = useState<
    string | undefined
  >();
  const lastNotifiedPage = useRef(currentPage);

  // Other hooks
  const { outOfScope } = useExtraImages();
  const { imageId: activeImageId } = useParams<{
    id: string;
    imageId?: string;
  }>();
  const images = useGetFilteredImages({});
  const lastImagesRef = useRef(images);

  const _imageTypesInBrowser: Record<ImageTypes, boolean> = useGetImageTypes();
  const currentImageFilter = useAtomValue(imageFilterAtom);
  const imageTypesInBrowser = useMemo(() => {
    // If we're in FMX view, filter out FMX images from the available types
    if (
      !activeImageId &&
      (currentImageFilter === annotatorConstants.FMX ||
        currentImageFilter === annotatorConstants.BITEWING)
    ) {
      if (_imageTypesInBrowser.fmxAsOther) {
        return _imageTypesInBrowser;
      }

      const filteredTypes = { ..._imageTypesInBrowser };
      filteredTypes.fmxPhotos = false;
      return filteredTypes;
    }
    return _imageTypesInBrowser;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_imageTypesInBrowser, currentImageFilter, activeImageId]);

  // Functions/Callbacks
  const filterSectionRef = useCallback((element: HTMLDivElement | null) => {
    if (element) {
      setFilterSectionWidth(element.offsetWidth);
    }
  }, []);

  const handleFilterClick = (filter: string) => {
    const filters = [filter];
    if (filter === 'fmxAsOther') {
      filters.push('fmxPhotos');
    }
    if (filter === 'fmxPhotos') {
      filters.push('fmxAsOther');
    }

    const newTypes = enabledImageTypes.includes(filter as ImageTypes)
      ? enabledImageTypes.filter((type) => !filters.includes(type))
      : [...enabledImageTypes, ...filters];

    setEnabledImageTypes(newTypes as ImageTypes[]);
    trackEvent(mixpanelEvents.imageGridFilterSelect, {
      filter,
      enabledFilters: newTypes,
      unifiedAppointmentId: visitSummary?.unifiedAppointmentId,
      estimatedVisitId: visitSummary?.estimatedVisitId,
    });
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      onPageChange(newPage);
    }
  };

  const handleShowAllClick = () => {
    const newTypes =
      enabledImageTypes.length === Object.keys(imageTypesInBrowser).length
        ? [] // If all filters are selected, deselect all
        : Object.entries(imageTypesInBrowser)
            .filter(([_, isEnabled]) => isEnabled)
            .map(([type]) => type as ImageTypes); // If some filters are deselected, select all

    setEnabledImageTypes(newTypes);
  };

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateNumberOfImages = () => {
      const rect = element.getBoundingClientRect();
      // Calculate how many images can fit, accounting for gaps and padding
      // When there's no active image, droppable containers are added which take up space
      // Each droppable container is 25px wide + 16px margin on each side = 57px total
      // Two containers = 114px additional space
      const droppableContainerSpace = !activeImageId ? 124 : 0;
      const availableWidth = rect.width - 196 - droppableContainerSpace;
      // Skip update when container hasn't been laid out yet (e.g. right after expand from collapse).
      // Otherwise we set a too-small count and one or more thumbnails disappear until reload.
      if (availableWidth < 86) return;
      const numberOfImagesThatFit = Math.max(
        1,
        Math.floor(availableWidth / 86),
      );
      setNumberOfImagesToDisplay(
        numberOfImagesThatFit > MAX_IMAGES_PER_PAGE
          ? MAX_IMAGES_PER_PAGE
          : numberOfImagesThatFit,
      );
    };

    // ResizeObserver fires when the element gets its real size (e.g. after expand layout).
    // Do not run a synchronous measurement on mount—the strip often has width 0 then.
    const resizeObserver = new ResizeObserver(() => {
      updateNumberOfImages();
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [setNumberOfImagesToDisplay, activeImageId]);

  // Computed values
  const displayImages = useMemo(
    () =>
      getImageBrowserDisplayImages({
        numberOfImagesToDisplay,
        images,
        page: currentPage,
      }),
    [numberOfImagesToDisplay, images, currentPage],
  );

  const totalPages = useMemo(
    () =>
      images?.length ? Math.ceil(images.length / numberOfImagesToDisplay) : 1,
    [images?.length, numberOfImagesToDisplay],
  );

  // useEffects
  // Find and navigate to the page containing the active image only on initial load
  useEffect(() => {
    if (!hasInitializedPage && activeImageId && images) {
      const activeImageIndex = images.findIndex(
        (image) => image.imageId === activeImageId,
      );
      if (activeImageIndex !== -1) {
        const pageNumber =
          Math.floor(activeImageIndex / numberOfImagesToDisplay) + 1;
        onPageChange(pageNumber);
        setHasInitializedPage(true);
      }
    }
  }, [
    activeImageId,
    images,
    numberOfImagesToDisplay,
    hasInitializedPage,
    onPageChange,
  ]);

  // Handle active image changes
  useEffect(() => {
    if (activeImageId && activeImageId !== lastActiveImageId && images) {
      setTimeout(() => {
        const activeImageIndex = images.findIndex(
          (image) => image.imageId === activeImageId,
        );
        if (activeImageIndex !== -1) {
          const pageNumber =
            Math.floor(activeImageIndex / numberOfImagesToDisplay) + 1;
          onPageChange(pageNumber);
        }
        setLastActiveImageId(activeImageId);
      }, 200);
    }
  }, [
    activeImageId,
    images,
    numberOfImagesToDisplay,
    lastActiveImageId,
    onPageChange,
  ]);

  useEffect(() => {
    // refresh filters when image types change
    if (
      !isEqual(prevImageTypes.current, imageTypesInBrowser) &&
      images?.length
    ) {
      setEnabledImageTypes(
        Object.entries(imageTypesInBrowser)
          .filter(([_, isEnabled]) => isEnabled)
          .map(([type]) => type as ImageTypes),
      );
      prevImageTypes.current = imageTypesInBrowser;
    }
  }, [imageTypesInBrowser, setEnabledImageTypes, images]);

  useEffect(() => {
    // only do this once, and after images have loaded
    if (
      !hasDecidedExpandedState.current &&
      images !== undefined // Ensure images have loaded
    ) {
      // if no images are available, collapse the image browser
      if (Object.values(imageTypesInBrowser).every((value) => !value)) {
        setIsExpanded(false);
      } else {
        setIsExpanded(true);
      }
      hasDecidedExpandedState.current = true;
    }
  }, [imageTypesInBrowser, setIsExpanded, images]);

  // Notify parent component of pagination changes
  useEffect(() => {
    if (onPaginationChange && lastNotifiedPage.current !== currentPage) {
      onPaginationChange({
        currentPage,
        totalPages,
        hasImages: !!(images && images.length > 0),
      });
      lastNotifiedPage.current = currentPage;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, onPaginationChange]);

  useEffect(() => {
    if (!isEqual(lastImagesRef.current, images)) {
      onPaginationChange?.({
        currentPage: 1,
        totalPages: totalPages,
        hasImages: !!images?.length,
      });
      lastImagesRef.current = images;
    }
  }, [totalPages, onPaginationChange, images]);

  const filterToImageTypes = useMemo<Record<ImageTypes, string[]>>(
    () => ({
      fmxPhotos: [t`X-RAY`],
      dentalPhotos: ['PHOTO'],
      panoPhotos: ['PANO'],
      otherPhotos: [t`OTHER`],
      fmxAsOther: [t`X-RAY`],
    }),
    [t],
  );

  if (!isExpanded) {
    return null;
  }

  // Render
  return (
    <div className="vai-flex vai-items-center vai-gap-4 vai-bg-background-gray vai-relative vai-px-4 vai-pb-4 vai-font-inter">
      {/* Filter Buttons */}
      <div ref={filterSectionRef} className="vai-flex vai-flex-col vai-gap-2">
        {Object.entries(imageTypesInBrowser)
          .filter(([key, hasImagesOfType]) => {
            // if there are fmxAsOther images and NO fmxPhotos, then we still want to show the XRAY filter
            if (key === 'fmxAsOther') {
              return hasImagesOfType && !imageTypesInBrowser.fmxPhotos;
            }
            return hasImagesOfType;
          })
          .map(([filter, hasImagesOfType]) => {
            const filterKey = filter as ImageTypes;
            const label = filterToImageTypes[filterKey]?.[0] || filter;
            return (
              <button
                data-testid={`image-filter-tab-${label}`}
                key={filter}
                className={classNames(
                  'vai-font-inter vai-font-bold vai-text-[10px] vai-rounded-sm vai-px-4 vai-py-1 vai-whitespace-nowrap focus:vai-outline-none',
                  !hasImagesOfType
                    ? 'vai-bg-zinc-800 vai-text-zinc-600 cursor-not-allowed'
                    : enabledImageTypes.includes(filterKey)
                      ? 'vai-bg-sapphire-600 vai-text-white hover:vai-bg-sapphire-700'
                      : 'vai-bg-zinc-800 vai-text-zinc-200 hover:vai-bg-zinc-700',
                )}
                onClick={() => handleFilterClick(filter)}
                disabled={!hasImagesOfType}
              >
                {label}
              </button>
            );
          })}
      </div>
      <div
        className="vai-flex vai-justify-center vai-w-full"
        style={{ marginRight: `${filterSectionWidth}px` }}
      >
        <div className="vai-flex vai-flex-col vai-w-full vai-items-center vai-gap-1 vai-px-4">
          {/* Image Strip with Arrows */}
          <div
            id="image-strip-scroll"
            ref={containerRef}
            className="vai-flex vai-items-center vai-w-full vai-bg-transparent vai-overflow-hidden vai-relative"
          >
            {/* Left Arrow */}
            {displayImages && displayImages.length > 0 && (
              <button
                className="vai-h-6 vai-flex-shrink-0 vai-flex vai-items-center vai-justify-center vai-rounded-md hover:vai-bg-zinc-900 vai-transition-colors vai-duration-200"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <FontAwesomeIcon
                  size="xl"
                  icon={faChevronLeft}
                  className={classNames(
                    currentPage === 1 ? 'vai-text-zinc-600' : 'vai-text-white',
                  )}
                />
              </button>
            )}
            <div className="vai-flex vai-flex-nowrap vai-items-center vai-justify-center vai-w-full vai-gap-2 vai-overflow-hidden vai-max-h-[64px]">
              {displayImages && displayImages.length > 0 ? (
                <SortableContext
                  items={displayImages.map((img) => img.imageId)}
                  strategy={rectSwappingStrategy}
                  id="image-grid"
                >
                  {!activeImageId && (
                    <DroppableContainer
                      key="image-grid-droppable-left"
                      id="image-grid-droppable-left"
                    />
                  )}
                  {displayImages.map((image, idx) => (
                    <Image image={image} index={idx} key={image.imageId} />
                  ))}
                  {currentPage === totalPages && isShowingAll && outOfScope && (
                    <button
                      className="vai-w-[70px] vai-h-[60px] vai-flex-shrink-0 vai-flex vai-items-center vai-justify-center vai-rounded-md vai-bg-zinc-800 hover:vai-bg-zinc-700 vai-transition-colors vai-duration-200"
                      onClick={handleShowAllClick}
                    >
                      <div className="vai-flex vai-flex-col vai-items-center vai-gap-1">
                        <span className="vai-text-sm vai-text-zinc-200">
                          {isShowingAll ? t`HIDE` : t`SHOW`}
                        </span>
                        <span className="vai-text-xs vai-text-zinc-400">
                          <Trans>EXTRA</Trans>
                        </span>
                        <Badge className="vai-mt-1 vai-rounded-full vai-text-xs vai-font-semibold vai-bg-green-600 vai-text-white vai-px-2 vai-py-0.5">
                          {outOfScope}
                        </Badge>
                      </div>
                    </button>
                  )}

                  {!activeImageId && (
                    <DroppableContainer
                      key="image-grid-droppable-right"
                      id="image-grid-droppable-right"
                    />
                  )}
                </SortableContext>
              ) : (
                <SortableContext
                  items={[]}
                  strategy={rectSwappingStrategy}
                  id="image-grid"
                >
                  <DroppableContainer
                    key="image-grid-droppable-left"
                    id="image-grid-droppable-left"
                  />
                  <div className="vai-text-zinc-400 vai-text-center vai-w-fit vai-h-[60px] vai-flex vai-items-center vai-justify-center">
                    <Trans>No images to display</Trans>
                  </div>
                  <DroppableContainer
                    key="image-grid-droppable-right"
                    id="image-grid-droppable-right"
                  />
                </SortableContext>
              )}
            </div>
            {/* Right Arrow */}
            {displayImages && displayImages.length > 0 && (
              <button
                className="vai-h-6 vai-flex-shrink-0 vai-flex vai-items-center vai-justify-center vai-rounded-md hover:vai-bg-zinc-900 vai-transition-colors vai-duration-200"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <FontAwesomeIcon
                  size="xl"
                  icon={faChevronRight}
                  className={classNames(
                    currentPage === totalPages
                      ? 'vai-text-zinc-600'
                      : 'vai-text-white',
                  )}
                />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
